import { css } from '@emotion/react';
import reset from './reset';
import theme from './theme';
import { mobile } from './media';

const { color, radius } = theme;

const FONT_FAMILY = `'Pretendard Variable', Pretendard, -apple-system, BlinkMacSystemFont, system-ui, 'Apple SD Gothic Neo', 'Noto Sans KR', sans-serif`;

const global = css`
  ${reset.styles}

  :root {
    color-scheme: light only;
    --toastify-color-dark: ${color.grey700};
    --toastify-text-color-dark: ${color.white};
    --toastify-color-success: ${color.success};
    --toastify-color-error: ${color.error};
    --toastify-color-info: ${color.info};
    --toastify-font-family: ${FONT_FAMILY};
    --toastify-toast-min-height: 48px;
    --toastify-toast-bd-radius: ${radius.pill}px;
  }

  html {
    -webkit-text-size-adjust: 100%;
  }

  body {
    font-family: ${FONT_FAMILY};
    color: ${color.text};
    background: ${color.background};
    letter-spacing: -0.01em;
  }

  input,
  select,
  textarea,
  button,
  pre {
    font-family: ${FONT_FAMILY};
    letter-spacing: inherit;
  }

  button {
    border: none;
    background: none;
    cursor: pointer;
    padding: 0;
    color: inherit;
  }

  hr {
    height: 0;
    margin: 0;
    background: unset;
  }

  a {
    color: inherit;
  }

  :focus-visible {
    outline: 2px solid ${color.grey300};
    outline-offset: 2px;
  }

  ::selection {
    background: ${color.grey100};
  }

  ::-webkit-scrollbar {
    width: 10px;
    height: 10px;
  }
  ::-webkit-scrollbar-thumb {
    background: ${color.grey200};
    border-radius: ${radius.pill}px;
    border: 2px solid transparent;
    background-clip: padding-box;
  }
  ::-webkit-scrollbar-track {
    background: transparent;
  }

  .font-number-semibold {
    font-family: 'PoppinsSemiBold';
    line-height: 1.2;
  }

  .font-number-bold {
    font-family: 'PoppinsBold';
    line-height: 1.2;
  }

  input:not([class]):not([type='checkbox']):not([type='radio']):not(
      [type='file']
    ):not([type='range']):not([type='color']),
  .react-datepicker__input-container > input,
  select:not([class]),
  textarea:not([class]) {
    height: 40px;
    padding: 0 12px;
    font-size: 14px;
    color: ${color.text};
    background: ${color.white};
    border: 1px solid ${color.grey100};
    border-radius: 10px;
    box-sizing: border-box;
    transition:
      border-color 0.15s ease,
      box-shadow 0.15s ease;
    &::placeholder {
      color: ${color.grey300};
    }
    &:focus {
      outline: none;
      border-color: ${color.primary};
      box-shadow: 0 0 0 3px ${color.grey75};
    }
    &:disabled {
      background: ${color.grey50};
      color: ${color.grey400};
    }
  }
  textarea:not([class]) {
    height: auto;
    padding: 12px;
    line-height: 1.6;
  }

  input[type='file'] {
    font-size: 13px;
    color: ${color.grey500};
    max-width: 100%;
  }
  input[type='file']::file-selector-button {
    margin-right: 10px;
    height: 36px;
    padding: 0 14px;
    border: none;
    border-radius: 10px;
    background: ${color.grey75};
    color: ${color.grey700};
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.15s ease;
  }
  input[type='file']::file-selector-button:hover {
    background: ${color.grey100};
  }

  .Toastify__toast-container {
    width: auto;
    min-width: 280px;
    max-width: 480px;
  }
  .Toastify__toast {
    padding: 12px 20px;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.4;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
    margin-bottom: 8px;
  }
  .Toastify__toast-icon {
    width: 18px;
  }
  .Toastify__close-button {
    align-self: center;
    opacity: 0.6;
  }
  ${mobile} {
    .Toastify__toast-container {
      min-width: 0;
      max-width: none;
      width: calc(100vw - 32px);
      left: 16px;
      right: 16px;
      transform: none;
      padding: 0;
      bottom: calc(16px + env(safe-area-inset-bottom));
    }
    .Toastify__toast {
      border-radius: ${radius.lg}px;
      margin-bottom: 8px;
    }
  }

  .react-datepicker {
    font-family: ${FONT_FAMILY};
    border: 1px solid ${color.border};
    border-radius: ${radius.md}px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    overflow: hidden;
  }
  .react-datepicker__header {
    background: ${color.white};
    border-bottom: 1px solid ${color.grey75};
  }
  .react-datepicker__day--selected,
  .react-datepicker__day--keyboard-selected,
  .react-datepicker__day--in-range,
  .react-datepicker__time-container
    .react-datepicker__time
    .react-datepicker__time-box
    ul.react-datepicker__time-list
    li.react-datepicker__time-list-item--selected {
    background: ${color.primary} !important;
    color: ${color.white} !important;
    border-radius: ${radius.sm}px;
  }
  .react-datepicker__day--in-selecting-range {
    background: ${color.grey100};
    color: ${color.text};
  }
  .react-datepicker__day:hover {
    border-radius: ${radius.sm}px;
  }

  .ql-toolbar.ql-snow {
    border-color: ${color.border};
    border-radius: ${radius.md}px ${radius.md}px 0 0;
  }
  .ql-container.ql-snow {
    border-color: ${color.border};
    border-radius: 0 0 ${radius.md}px ${radius.md}px;
  }
`;

export default global;
